import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import * as ToastPrimitive from '@radix-ui/react-toast'
import { AlertCircle, AlertTriangle, Check, Info, X } from 'lucide-react'
import { cn } from '@/utils'
import { TOAST_EVENTS } from '@/constants'

// ─── Types ───────────────────────────────────────────────────────────────────

export type ToastVariant = 'default' | 'success' | 'warning' | 'error'

export interface ToastItem {
  id: string
  title: string
  description?: string
  variant: ToastVariant
  duration: number
}

interface ToastContextValue {
  toast: (options: Omit<ToastItem, 'id' | 'duration'> & { duration?: number }) => void
  dismiss: (id: string) => void
}

// ─── Context ─────────────────────────────────────────────────────────────────

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

// ─── Variant config ──────────────────────────────────────────────────────────

const variantConfig: Record<
  ToastVariant,
  { icon: React.ElementType; borderCls: string; iconCls: string }
> = {
  default: { icon: Info, borderCls: 'border-border', iconCls: 'text-muted-foreground' },
  success: { icon: Check, borderCls: 'border-success/30', iconCls: 'text-success' },
  warning: { icon: AlertTriangle, borderCls: 'border-warning/30', iconCls: 'text-warning' },
  error: { icon: AlertCircle, borderCls: 'border-destructive/30', iconCls: 'text-destructive' },
}

// ─── Toast item component ─────────────────────────────────────────────────────

interface ToastItemProps {
  item: ToastItem
  onClose: () => void
}

function ToastItemComponent({ item, onClose }: ToastItemProps) {
  const config = variantConfig[item.variant]
  const Icon = config.icon

  return (
    <ToastPrimitive.Root
      open
      duration={item.duration}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      className={cn(
        'flex w-full max-w-sm items-start gap-3 rounded-xl border bg-card p-4 shadow-lg',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-right-full',
        config.borderCls,
      )}
    >
      <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', config.iconCls)} aria-hidden="true" />

      <div className="flex-1">
        <ToastPrimitive.Title className="text-sm font-medium text-foreground">
          {item.title}
        </ToastPrimitive.Title>
        {item.description && (
          <ToastPrimitive.Description className="mt-0.5 text-xs text-muted-foreground">
            {item.description}
          </ToastPrimitive.Description>
        )}
      </div>

      <ToastPrimitive.Close asChild>
        <button
          type="button"
          className="shrink-0 rounded-md p-0.5 text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring"
          aria-label="Cerrar notificación"
        >
          <X className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      </ToastPrimitive.Close>
    </ToastPrimitive.Root>
  )
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const toast = useCallback(
    (options: Omit<ToastItem, 'id' | 'duration'> & { duration?: number }) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`
      setToasts((prev) => [
        ...prev,
        { ...options, id, duration: options.duration ?? 4000 },
      ])
    },
    [],
  )

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  useEffect(() => {
    function handleToastEvent(event: Event): void {
      const { variant, title, description } = (
        event as CustomEvent<{ variant: ToastVariant; title: string; description?: string }>
      ).detail
      toast({ variant, title, description })
    }
    window.addEventListener(TOAST_EVENTS.SHOW, handleToastEvent)
    return () => window.removeEventListener(TOAST_EVENTS.SHOW, handleToastEvent)
  }, [toast])

  return (
    <ToastContext.Provider value={{ toast, dismiss }}>
      <ToastPrimitive.Provider swipeDirection="right">
        {children}
        {toasts.map((t) => (
          <ToastItemComponent key={t.id} item={t} onClose={() => dismiss(t.id)} />
        ))}
        <ToastPrimitive.Viewport className="fixed bottom-4 right-4 z-[100] flex max-w-sm flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  )
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useToastContext(): ToastContextValue {
  const context = useContext(ToastContext)
  if (!context) throw new Error('useToastContext must be used within a ToastProvider')
  return context
}
